import { useQuery } from '@apollo/client';
import { StatusQuoAnalysisBuildingGetBuildingQuery } from '@predium/client-graphql';
import isNil from 'lodash/isNil';
import { useSnackbar } from 'notistack';
import { useQueryStates } from 'nuqs';
import { createStandardErrorSnackbar } from '@/components/NotistackProvider';
import { getDefaultReferenceArea } from '@/components/organisms/AnalysisSettings/machines/analysisSettingsMachine';
import { GET_ANALYZED_BUILDING } from '@/graphql/StatusQuoAnalysis.queries';
import { SEARCH_PARAMS } from '@/routes/search-params';
import { StatusQuoAnalysisTabEnum } from '@/routes/search_params/searchParamsEnums';

type Options = {
  buildingId: number;
  onCompleted?: (data: StatusQuoAnalysisBuildingGetBuildingQuery) => void;
  /** Skip the query — e.g. for planned (new construction) buildings, which analysis doesn't support yet. */
  skip?: boolean;
};

export const useQueryGetAnalyzedBuilding = ({ buildingId, onCompleted, skip = false }: Options) => {
  const displayStandardMutationQueryErrorSnackbar = createStandardErrorSnackbar();
  const { enqueueSnackbar } = useSnackbar();
  const [{ referenceAreaType, emissionFactorSource, tab }] = useQueryStates(SEARCH_PARAMS.statusQuoAnalysis.building);
  const currentYear = new Date().getFullYear();

  // Fall back to the tab default so the query fires immediately on first load,
  // before AnalysisSettings writes the user's preference to the URL.
  const isConsumptionTab = tab === StatusQuoAnalysisTabEnum.CONSUMPTION;
  const effectiveReferenceAreaType = isNil(referenceAreaType)
    ? getDefaultReferenceArea(isConsumptionTab)
    : referenceAreaType;

  const { data, loading, previousData } = useQuery(GET_ANALYZED_BUILDING, {
    fetchPolicy: 'cache-and-network',
    variables: {
      buildingId,
      year: currentYear,
      referenceAreaType: effectiveReferenceAreaType,
      emissionFactorSource,
    },
    skip,
    onCompleted,
    onError: () => displayStandardMutationQueryErrorSnackbar(enqueueSnackbar),
  });

  return {
    analyzedBuilding: data ? data.getAnalyzedBuilding : previousData?.getAnalyzedBuilding,
    isLoadingAnalyzedBuilding: loading,
  };
};
